import React from 'react';
import ReactMarkdown from "react-markdown";
import { useState } from 'react';
// import { BotMessageSquare } from "lucide-react";
import { VegaEmbed } from "react-vega";
import { Copy } from "lucide-react";
import { SquareCheckBig, ThumbsUp, ThumbsDown, Paperclip } from "lucide-react";
import ReadAloud from "./ReadAloud";
import OGFleetIcon from '../../../../assets/OGFLEET Logo(1).png';
import toast from 'react-hot-toast';
import { storeMessageFeedback } from '../../../api';

// Helper to removes JS comments
const removeJsonComments = (jsonString) => {
  let insideString = false;
  let result = "";
  
  for (let i = 0; i < jsonString.length; i++) {
    const current = jsonString[i];
    const next = jsonString[i + 1];

    if (current === '"' && jsonString[i - 1] !== '\\') {
      insideString = !insideString;
    }

    // Remove // comments (only if not inside string, to preserve eg. https://)
    if (!insideString && current === '/' && next === '/') {
      while (i < jsonString.length && jsonString[i] !== '\n') i++;
      continue;
    }

    // Remove /* */ comments (only if not inside string)
    if (!insideString && current === '/' && next === '*') {
      i += 2;
      while (i < jsonString.length && !(jsonString[i] === '*' && jsonString[i + 1] === '/')) {
        i++;
      }
      i++;
      continue;
    }

    result += current;
  }

  return result;
};


const CopyButton = ({ text, loading, isDarkTheme }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 5000); // Reset after 2 seconds
    });
  };

  return (
    <button
      onClick={handleCopy}
      className={`relative mt-2 transition-colors ${isDarkTheme ? "text-gray-400 hover:text-amber-500" : "text-gray-500 hover:text-amber-500"
        }`}
      title={copied ? 'Copied!' : 'Copy to clipboard'}
    >
      {copied ? (
        <SquareCheckBig className="w-4 h-4" />
      ) : (
        <Copy className="w-4 h-4" />
      )}
    </button>
  );
};

const FeedbackButtons = ({ messageId, onLike, onDislike, isDarkTheme }) => {
  const [liked, setLiked] = useState(false);
  const [disliked, setDisliked] = useState(false);

  const handleLike = async () => {
    if (!liked) {
      try {
        await storeMessageFeedback(messageId, 'positive');
        setLiked(true);
        setDisliked(false);
        toast.success("Thank you for your feedback!", {
          duration: 3000,
        });
        onLike(true);
      } catch (err) {
        toast.error("Failed to send feedback");
      }
    }
  };

  const handleDislike = async () => {
    if (!disliked) {
      try {
        await storeMessageFeedback(messageId, 'negative');
        setDisliked(true);
        setLiked(false);
        toast.success("Thank you for your feedback!", {
          duration: 3000,
        });
        onDislike(true);
      } catch (err) {
        toast.error("Failed to send feedback");
      }
    }
  };

  return (
    <div className="flex items-center gap-3 mt-2">
      <button
        onClick={handleLike}
        className={`transition-colors ${isDarkTheme ? "text-gray-400 hover:text-amber-500" : "text-gray-500 hover:text-amber-500"
          } ${liked ? '!text-amber-500' : ''}`}
        title="Like"
      >
        <ThumbsUp className="w-4 h-4" />
      </button>
      <button
        onClick={handleDislike}
        className={`transition-colors ${isDarkTheme ? "text-gray-400 hover:text-amber-500" : "text-gray-500 hover:text-amber-500"
          } ${disliked ? '!text-amber-500' : ''}`}
        title="Dislike"
      >
        <ThumbsDown className="w-4 h-4" />
      </button>
    </div>
  );
};

const Message = ({ messageId, content, sender, loading, isDarkTheme }) => {
  const [liked, setLiked] = useState(false);
  const [disliked, setDisliked] = useState(false);

  let parsedText = content;
  let chartSpec = null;

  // --- CHART TAG EXTRACTION ---
  const chartStart = "<tag_chart>";
  const chartEnd = "</tag_chart>";

  const startIdx = content.indexOf(chartStart);
  const endIdx = content.indexOf(chartEnd);

  if (startIdx !== -1) {
    const beforeChart = content.slice(0, startIdx).trim();
    const afterChart =
      endIdx !== -1
        ? content.slice(endIdx + chartEnd.length).trim()
        : "";

    // chart is still streaming
    if (endIdx === -1) {
      parsedText =
        beforeChart +
        (beforeChart ? "\n\n" : "") +
        "__CHART_LOADING__" +
        (afterChart ? "\n\n" + afterChart : "");
    }

    // chart is complete
    if (endIdx !== -1) {
      const chartJson = content
        .slice(startIdx + chartStart.length, endIdx)
        .trim();

      try {
        const cleanedChartJson = removeJsonComments(chartJson);
        chartSpec = JSON.parse(cleanedChartJson);
      } catch (e) {
        // invalid chart JSON → ignore chart
      }

      // keep chart position using placeholder
      parsedText =
        beforeChart +
        (beforeChart ? "\n\n" : "") +
        "__CHART_PLACEHOLDER__" +
        (afterChart ? "\n\n" + afterChart : "");

    }
  }


  // --- EXISTING LOGIC (UNCHANGED, BUT GUARDED) ---
  if (!chartSpec) {
    try {
      const cleanedContent = removeJsonComments(content);
      const parsed = JSON.parse(cleanedContent);

      // if it has 'text' field, use it
      if (parsed.text) {
        parsedText = parsed.text;
      }
      // if it's a valid Vega-Lite object (has 'mark' and 'encoding'), use it as chart
      if (parsed.mark && parsed.encoding) {
        chartSpec = {
          $schema: "https://vega.github.io/schema/vega-lite/v5.json",
          ...parsed,
        };
      }
    } catch (e) {
      // not JSON, leave parsedText as content
    }
  }

  const handleLike = (isLiked) => { setLiked(isLiked); setDisliked(false); };
  const handleDislike = (isDisliked) => { setDisliked(isDisliked); setLiked(false); };

  if (sender === 'file') {
    // Extract filename from the content
    const contentParts = content.split("Extracted Content");
    const filename = contentParts[0].trim() || "Unknown File";
    const fileExtension = filename.split('.').pop().toLowerCase() || '';

    // Get file type label based on extension
    const getFileType = (ext) => {
      const types = {
        pdf: 'PDF',
        doc: 'DOC',
        docx: 'DOCX',
        xls: 'XLS',
        xlsx: 'XLSX',
        txt: 'TXT',
        jpg: 'JPG',
        jpeg: 'JPEG',
        png: 'PNG',
        // Add more file types as needed
      };
      return types[ext] || '';
    };

    const fileType = getFileType(fileExtension);

    return (
      <div className={`${isDarkTheme
        ? 'bg-gray-800 border-gray-700 text-gray-200 shadow-lg shadow-gray-900/30'
        : 'bg-white border-gray-200 text-gray-800 shadow-md shadow-gray-200/50'} 
        py-2 px-4 rounded-lg border self-end max-w-md mt-2 mb-1 transition-all`}>
        <div className="flex items-center">
          <div className="flex-shrink-0 mr-3">
            <div className={`${isDarkTheme
              ? 'bg-gray-700'
              : 'bg-gray-100'} 
              p-2 rounded-md flex items-center justify-center`}>
              <Paperclip className={`w-5 h-5 ${isDarkTheme ? 'text-amber-400' : 'text-amber-500'}`} />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">{filename}</p>
            {fileType && (
              <p className={`text-xs ${isDarkTheme ? 'text-amber-400/80' : 'text-amber-600/80'} mt-1`}>
                {fileType}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
    style={{ 
      wordBreak: "break-word", 
      overflowWrap: "break-word" 
    }}
    className={`message rounded-lg break-words whitespace-pre-line flex ${sender === 'user'
      ? isDarkTheme
        ? 'bg-gray-700 border-l-4 border-amber-400/80 text-gray-300 self-end max-w-[75%] m-2 p-3 shadow-sm'
        : 'bg-amber-50 border-l-4 border-amber-400 text-gray-800 self-end max-w-[75%] m-2 p-3 shadow-sm'
      : isDarkTheme
        ? 'text-gray-300 self-start max-w-[100%] m-2 p-3 shadow-sm'
        : 'bg-white text-gray-800 self-start max-w-[100%] m-2 p-3 shadow-sm'
      }`}>

      {sender !== 'user' && (
        <div className="flex-shrink-0 mr-2">
          <div>
            <img
              src={OGFleetIcon}
              alt="OGFLEET Icon"
              className={`w-[40px] h-[33px] -mt-1`}
            />
          </div>
        </div>
      )}


      <div className="flex-1">
        {parsedText.split("__CHART_PLACEHOLDER__").map((part, index, arr) => (
          <React.Fragment key={index}>
            <ReactMarkdown
              components={{
                pre: ({ node, ...props }) => (
                  <pre
                    className={`whitespace-pre-wrap break-words overflow-x-auto p-3 rounded-md max-w-full border-l-4 border-amber-400/80 my-2 ${
                      isDarkTheme
                        ? 'bg-gray-700 text-gray-300'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                    {...props}
                  />
                ),
                code: ({ node, inline, ...props }) => (
                  <code
                    className={`${
                      inline ? 'px-1 py-0.5 rounded-md' : 'p-2 rounded-md'
                    } ${
                      isDarkTheme
                        ? 'bg-gray-700 text-gray-300'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                    {...props}
                  />
                ),
                a: ({ node, ...props }) => (
                  <a
                    className={`${
                      isDarkTheme
                        ? 'text-amber-400 hover:text-amber-300'
                        : 'text-amber-600 hover:text-amber-800'
                    } underline`}
                    {...props}
                  />
                ),
                h1: ({ node, ...props }) => (
                  <h1
                    className={`text-2xl font-bold ${
                      isDarkTheme ? 'text-gray-300' : 'text-gray-900'
                    } my-4`}
                    {...props}
                  />
                ),
                h2: ({ node, ...props }) => (
                  <h2
                    className={`text-xl font-bold ${
                      isDarkTheme ? 'text-gray-300' : 'text-gray-900'
                    } my-3`}
                    {...props}
                  />
                ),
                h3: ({ node, ...props }) => (
                  <h3
                    className={`text-lg font-bold ${
                      isDarkTheme ? 'text-gray-300' : 'text-gray-900'
                    } my-2`}
                    {...props}
                  />
                ),
                ul: ({ node, ...props }) => (
                  <ul className="list-disc pl-5 my-2 space-y-1" {...props} />
                ),
                ol: ({ node, ...props }) => (
                  <ol className="list-decimal pl-5 my-2 space-y-1" {...props} />
                ),
              }}
            >
              {part.replace("__CHART_LOADING__", "⏳ Generating chart…")}
            </ReactMarkdown>

            {index < arr.length - 1 && chartSpec && (
              <div className="mt-3">
                <VegaEmbed
                  spec={chartSpec}
                  options={{ mode: "vega-lite", actions: false }}
                />
              </div>
            )}
          </React.Fragment>
        ))}


        {sender !== 'user' && (
          <div className="flex items-center gap-3 mt-2">
            <CopyButton text={parsedText} loading={loading} />
            <FeedbackButtons messageId={messageId} onLike={handleLike} onDislike={handleDislike} />
            <ReadAloud text={parsedText} isDarkTheme={isDarkTheme} />
          </div>
        )}
      </div>
    </div>
  );
};

export default Message;
